export const UPLOAD_IMAGE_TARGET_BYTES = 512 * 1024;
export const UPLOAD_IMAGE_MAX_BYTES = 3 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

const QUALITY_STEP = 0.08;
const MIN_QUALITY = 0.52;
const SCALE_STEP = 0.86;
const MIN_SCALE = 0.45;
const MAX_ATTEMPTS = 12;

const normalizeType = (type) => (type === "image/jpg" ? "image/jpeg" : type);

const getOutputType = (type) => {
  const normalizedType = normalizeType(type);
  if (normalizedType === "image/png") {
    return "image/webp";
  }
  return normalizedType;
};

const getExtension = (type) => {
  switch (normalizeType(type)) {
    case "image/jpeg":
      return ".jpg";
    case "image/png":
      return ".png";
    case "image/webp":
      return ".webp";
    default:
      return "";
  }
};

const renameFile = (filename, type) => {
  const ext = getExtension(type);
  if (!ext) {
    return filename;
  }
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex === -1) {
    return `${filename}${ext}`;
  }
  return `${filename.slice(0, dotIndex)}${ext}`;
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image."));
    };

    image.src = objectUrl;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to compress image."));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });

const toFile = (blob, originalName, type) =>
  new File([blob], renameFile(originalName, type), {
    type,
    lastModified: Date.now(),
  });

const renderCompressedBlob = async (image, scale, type, quality) => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is not supported in this browser.");
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, type, quality);
};

export const isAllowedUploadImageType = (type) => ALLOWED_IMAGE_TYPES.has(type);

export const compressUploadImage = async (file, targetBytes = UPLOAD_IMAGE_TARGET_BYTES) => {
  const normalizedType = normalizeType(file.type);
  if (!isAllowedUploadImageType(normalizedType)) {
    throw new Error("仅支持 JPG、PNG、WEBP 图片");
  }

  if (file.size <= targetBytes) {
    return file;
  }

  const image = await loadImage(file);
  let outputType = getOutputType(normalizedType);
  let quality = outputType === "image/png" ? undefined : 0.92;
  let scale = 1;
  let bestBlob = file;
  let bestType = normalizedType;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const blob = await renderCompressedBlob(image, scale, outputType, quality);
    if (blob.size < bestBlob.size) {
      bestBlob = blob;
      bestType = outputType;
    }
    if (blob.size <= targetBytes) {
      return toFile(blob, file.name, outputType);
    }

    if (quality !== undefined && quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
      continue;
    }

    scale *= SCALE_STEP;
    if (scale < MIN_SCALE && outputType !== "image/webp") {
      outputType = "image/webp";
      quality = 0.86;
    }
  }

  if (bestBlob === file) {
    return file;
  }
  return toFile(bestBlob, file.name, bestType);
};
